import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { ReportsApi, type StudentSummaryRow } from '@/services/reports.api';
import { AcademicYearsApi, AcademicClassesApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<StudentSummaryRow>();

/** Enrolled-student rollup by class/section/gender/status — a genuinely new aggregate query, no existing screen does this (the Students list is flat per-student). */
export function StudentSummaryReportPage() {
  const [yearId, setYearId] = useState('');
  const [classId, setClassId] = useState('');
  const dt = useDataTable({ defaultLimit: 20 });

  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-student-summary'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];

  const { data, isFetching } = useQuery({
    queryKey: ['reports', 'student-summary', yearId, classId],
    queryFn: () => ReportsApi.studentSummary({ academicYearId: yearId || undefined, classId: classId || undefined }),
  });

  // Reflect the server's own default (is_current year) into the selector once it resolves.
  useEffect(() => {
    if (!yearId && data?.academicYearId) setYearId(data.academicYearId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.academicYearId]);

  // Bounded, pre-aggregated rollup (one row per class×section, never more
  // than a few dozen) — paginate client-side over the already-loaded full
  // array rather than adding server-side pagination for a result set this
  // small, same pattern as OrgSchoolFeesSummaryPage.
  const allRows = data?.rows ?? [];
  const total = allRows.length;
  const totalPages = Math.max(1, Math.ceil(total / dt.limit));
  const page = Math.min(dt.page, totalPages);
  const pageRows = allRows.slice((page - 1) * dt.limit, page * dt.limit);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'no',
        header: 'No.',
        cell: (info) => <span className="text-slate-400">{(page - 1) * dt.limit + info.row.index + 1}</span>,
      }),
      columnHelper.accessor('className', { id: 'className', header: 'Class' }),
      columnHelper.accessor('sectionName', { id: 'sectionName', header: 'Section' }),
      columnHelper.accessor('total', { id: 'total', header: 'Total', cell: (info) => <span className="font-medium text-slate-900">{info.getValue()}</span> }),
      columnHelper.accessor('male', { id: 'male', header: 'Male' }),
      columnHelper.accessor('female', { id: 'female', header: 'Female' }),
      columnHelper.accessor('other', { id: 'other', header: 'Other' }),
      columnHelper.accessor('active', { id: 'active', header: 'Active', cell: (info) => <span className="text-green-700">{info.getValue()}</span> }),
      columnHelper.accessor('left', { id: 'left', header: 'Left', cell: (info) => <span className="text-slate-500">{info.getValue()}</span> }),
    ],
    [page, dt.limit],
  );

  return (
    <>
      <PageHeader title="Student Summary Report" description="Enrolled students by class, section, gender, and status." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-44" value={yearId} onChange={(e) => setYearId(e.target.value)}>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
        <Select className="!w-40" value={classId} onChange={(e) => setClassId(e.target.value)}>
          <option value="">All Classes</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </div>

      {data && data.rows.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total</div>
            <div className="text-xl font-bold text-slate-800">{data.totals.total}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Male</div>
            <div className="text-xl font-bold text-slate-800">{data.totals.male}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Female</div>
            <div className="text-xl font-bold text-slate-800">{data.totals.female}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Active</div>
            <div className="text-xl font-bold text-green-700">{data.totals.active}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Left</div>
            <div className="text-xl font-bold text-slate-500">{data.totals.left}</div>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        result={{ items: pageRows, total, page, limit: dt.limit, totalPages }}
        state={dt}
        loading={isFetching}
        emptyMessage="No enrolled students for the selected filters."
      />
    </>
  );
}
